import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Switch,
  Typography,
  Box,
  Divider,
} from '@mui/material';
import { Gear } from '@phosphor-icons/react';
import { DragIndicator } from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { CARD_REGISTRY } from './dashboardCards/cardRegistry';

function DashboardCardManager({ 
  open, 
  onClose, 
  enabledCards, 
  cardOrder,
  onToggleCard,
  onCardOrderChange 
}) {
  const [orderedEnabledCards, setOrderedEnabledCards] = useState([]);
  const allCards = Object.values(CARD_REGISTRY);

  // Update ordered enabled cards when dialog opens or dependencies change
  useEffect(() => {
    if (open) {
      // Get enabled cards in their current order
      const ordered = cardOrder
        .map(id => CARD_REGISTRY[id])
        .filter(card => card && enabledCards.includes(card.id));
      
      // Add any enabled cards that aren't in the order yet
      const orderedIds = new Set(ordered.map(c => c.id));
      const missingCards = enabledCards
        .map(id => CARD_REGISTRY[id])
        .filter(card => card && !orderedIds.has(card.id));
      
      setOrderedEnabledCards([...ordered, ...missingCards]);
    }
  }, [open, enabledCards, cardOrder]);

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    if (result.destination.index === result.source.index) return;

    const items = Array.from(orderedEnabledCards);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setOrderedEnabledCards(items);
    
    // Update the order: reordered enabled cards + disabled cards that were already in order
    const disabledCardsInOrder = cardOrder.filter(id => !enabledCards.includes(id));
    const reorderedIds = items.map(card => card.id);
    const finalOrder = [...reorderedIds, ...disabledCardsInOrder];
    
    onCardOrderChange(finalOrder);
  };

  // Get disabled cards (not in enabled list)
  const disabledCards = allCards.filter(card => !enabledCards.includes(card.id));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Gear size={24} weight="regular" />
          <Typography variant="h6">Customise your dashboard</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Drag to reorder. Use the toggles to show or hide sections on your dashboard.
        </Typography>
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="card-manager-list">
            {(provided, snapshot) => (
              <List
                ref={provided.innerRef}
                {...provided.droppableProps}
                sx={{
                  backgroundColor: snapshot.isDraggingOver ? 'action.hover' : 'transparent',
                  borderRadius: snapshot.isDraggingOver ? 1 : 0,
                  transition: 'background-color 0.2s ease',
                }}
              >
                {/* Enabled cards (draggable) */}
                {orderedEnabledCards.map((card, index) => (
                  <Draggable
                    key={card.id}
                    draggableId={card.id}
                    index={index}
                  >
                    {(provided, snapshot) => (
                      <React.Fragment>
                        <ListItem
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          sx={{
                            backgroundColor: snapshot.isDragging ? 'action.selected' : 'transparent',
                            opacity: snapshot.isDragging ? 0.8 : 1,
                            transition: 'all 0.2s ease',
                          }}
                        >
                          <Box
                            {...provided.dragHandleProps}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              cursor: 'grab',
                              mr: 1,
                              color: 'text.secondary',
                              '&:active': {
                                cursor: 'grabbing',
                              },
                            }}
                          >
                            <DragIndicator fontSize="small" />
                          </Box>
                          <ListItemText
                            primary={card.title}
                            secondary={
                              <Typography variant="caption" color="text.secondary">
                                {card.category}
                              </Typography>
                            }
                          />
                          <ListItemSecondaryAction>
                            <Switch
                              edge="end"
                              checked={true}
                              onChange={() => onToggleCard(card.id)}
                            />
                          </ListItemSecondaryAction>
                        </ListItem>
                        {index < orderedEnabledCards.length - 1 && <Divider />}
                      </React.Fragment>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
                
                {/* Disabled cards (not draggable) */}
                {disabledCards.length > 0 && orderedEnabledCards.length > 0 && (
                  <Divider sx={{ my: 1 }} />
                )}
                {disabledCards.map((card, index) => (
                  <React.Fragment key={card.id}>
                    <ListItem>
                      <Box sx={{ width: 20, mr: 1 }} />
                      <ListItemText
                        primary={card.title}
                        secondary={
                          <Typography variant="caption" color="text.secondary">
                            {card.category}
                          </Typography>
                        }
                      />
                      <ListItemSecondaryAction>
                        <Switch
                          edge="end"
                          checked={false}
                          onChange={() => onToggleCard(card.id)}
                        />
                      </ListItemSecondaryAction>
                    </ListItem>
                    {index < disabledCards.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            )}
          </Droppable>
        </DragDropContext>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Done</Button>
      </DialogActions>
    </Dialog>
  );
}

export default DashboardCardManager;
